import React, { useState } from 'react';
import { GeneralModal } from 'ui/GeneralModal';
import { TextInput } from 'ui/TextInput';
import FluidButton from 'ui/FluidButton';
import Checkbox from 'ui/Checkbox';
import { useFilesInput } from 'hooks/useFilesInput';
import { useVoucherUploader } from '../useVoucherUploader';
import { ENetworkRequestStatus } from 'services/BackendApi';
import { useNotifications } from 'hooks/useNotifications';

interface IVoucherAddUploadModalProps {
  voucherUuid: string;
  isOpen: boolean;
  onClose: () => void;
  uploadType: 'image' | 'file';
  handleConfirm: (data?: any) => void;
  modalTitle: string;
  displayNameOverwrite?: string;
  productType?: string;
  isForLogo?: boolean;
  voucherData?: any;
}

export const VoucherAddUploadModal: React.FC<IVoucherAddUploadModalProps> = ({
  voucherUuid,
  isOpen,
  onClose,
  uploadType,
  handleConfirm,
  modalTitle,
  displayNameOverwrite,
  productType,
  isForLogo,
  voucherData,
}) => {
  const [displayName, setDisplayName] = useState<string>('');
  const [noLogo, setNoLogo] = useState<boolean>(false);
  const voucherUploader = useVoucherUploader(voucherUuid);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const filesInput = useFilesInput({
    accept: uploadType === 'image' ? 'image/*' : '',
  });

  const handleClose = () => {
    // Resolve with undefined to preserve existing logo (cancel/close without changes)
    // If handleConfirm was already called (e.g., "No Logo" clicked), this does nothing
    // since promises can only resolve once
    handleConfirm(undefined);
    filesInput.reset();
    setDisplayName('');
    setNoLogo(false);
  };

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      modalWindowClassName="p-4 text-black font-pt-sans w-[587px]"
      isCloseButtonVisible={false}
    >
      <h2 className="m-0 font-noe-display text-21px text-black font-normal mb-25px">{modalTitle}</h2>

      {displayNameOverwrite === undefined && !noLogo && (
        <>
          <span className="block">Display Name</span>
          <TextInput
            id="upload-modal-display-name"
            value={displayName}
            onChange={e => setDisplayName(e.currentTarget.value)}
          />
        </>
      )}

      {!noLogo && (
        <>
          <span className="block mt-10px">File</span>
          {filesInput.renderInput()}
        </>
      )}

      {isForLogo && (
        <div className="flex gap-5px items-center my-20px">
          <Checkbox id="no-logo-checkbox" checked={noLogo} onChange={e => setNoLogo(e.currentTarget.checked)} />
          <label htmlFor="no-logo-checkbox" className="ml-10px">
            No Logo
          </label>
        </div>
      )}

      <div className="flex flex-row gap-2 mt-25px">
        <FluidButton
          disabled={
            !noLogo && (filesInput.files.length === 0 || (displayName === '' && displayNameOverwrite === undefined))
          }
          type="primary"
          isLoading={voucherUploader.requestStatus === ENetworkRequestStatus.PENDING}
          className="mt-10px"
          onClick={async () => {
            if (noLogo) {
              // When "No Logo" is checked, update parent state to remove logo
              // The actual save will happen when user clicks the main Save button
              handleConfirm(null);
              handleClose();
            } else {
              // Normal upload flow
              const file = filesInput.files[0];
              if (!file) return;

              const output = await voucherUploader.uploadAndProcessFile({
                file,
                displayName: displayNameOverwrite || displayName,
                fileType: uploadType,
              });

              if (output) {
                // Success path - upload returned valid data
                try {
                  handleConfirm(output);
                  // Show success notification if productType is provided
                  if (productType) {
                    showSuccessNotification(`Attachment added to ${productType}`);
                  }
                  handleClose();
                } catch (e) {
                  console.error(e);
                  showErrorNotification(`Failed to add ${uploadType === 'image' ? 'image' : 'file'}`);
                  handleClose();
                }
              } else {
                // Failure path - upload returned null
                // Pass undefined (not null) to handleConfirm so handleLogoUpload ignores it
                // null is reserved for "No Logo" button (intentional removal)
                // undefined means "no change" (preserves existing logo)
                showErrorNotification(`Failed to upload ${uploadType === 'image' ? 'image' : 'file'}`);
                handleConfirm(undefined);
                // Do cleanup
                filesInput.reset();
                setDisplayName('');
                setNoLogo(false);
              }
            }
          }}
        >
          {noLogo ? 'Remove Logo' : uploadType === 'image' ? 'Upload & Add Image' : 'Upload & Add File'}
        </FluidButton>

        <FluidButton type="secondary" className="mt-10px" onClick={handleClose}>
          Cancel
        </FluidButton>
      </div>
    </GeneralModal>
  );
};
